import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { useDialog } from '@shared/hooks/useDialog';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getStorageTypeTone } from '@shared/config/theme';
import { toneChipClasses, toneTextClasses } from '@shared/utils/statusTone';
import {
  Database,
  Plus,
  Trash2,
  Server,
  Cloud,
  Box,
  HardDrive,
  Activity,
  X,
  Shield,
  Settings,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Wifi,
  ChevronRight,
  ChevronLeft,
  Clock,
  RotateCcw,
  AlertTriangle,
  RefreshCw,
  TrendingDown,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StorageDestination {
  id: string;
  name: string;
  type: string;
  is_default: boolean;
  storage_quota_gb?: number;
  storage_used_gb: number;
  monthly_cost?: number;
  credentials?: Record<string, unknown>;
  config?: Record<string, unknown>;
  created_at: string;
}

interface PlatformPlan {
  name: string;
  quota_gb: number;
  monthly_cost: number;
  currency: string;
}

interface CredentialHistoryEntry {
  id: string;
  version: number;
  changed_fields: string[];
  changed_by?: string;
  changed_by_name?: string;
  change_reason?: string;
  created_at: string;
}

interface ConfirmationInfo {
  changing_fields: string[];
  has_restic_password_change: boolean;
  warning: string;
}

type StorageType = 'PLATFORM' | 'S3' | 'AZURE_BLOB' | 'GCS';

interface VaultForm {
  name: string;
  is_default: boolean;
  // Common (restic repo encryption password)
  restic_password: string;
  // S3
  bucket_name: string;
  region: string;
  access_key_id: string;
  secret_access_key: string;
  endpoint: string;
  // Azure
  storage_account_name: string;
  container_name: string;
  access_key: string;
  // GCS
  gcs_bucket_name: string;
  json_key: string;
  // Platform
  selected_plan_gb: number;
}

const EMPTY_FORM: VaultForm = {
  name: '',
  is_default: false,
  restic_password: '',
  bucket_name: '',
  region: '',
  access_key_id: '',
  secret_access_key: '',
  endpoint: '',
  storage_account_name: '',
  container_name: '',
  access_key: '',
  gcs_bucket_name: '',
  json_key: '',
  selected_plan_gb: 0,
};

// Normalize type from DB (may be lowercase) to our expected uppercase format
const normalizeType = (t: string): StorageType => t.toUpperCase().replace(' ', '_') as StorageType;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function StorageDestinations() {
  const { t, i18n } = useTranslation('storage');
  const locale = i18n.resolvedLanguage || i18n.language;
  const { showError, showConfirm } = useDialog();
  const [destinations, setDestinations] = useState<StorageDestination[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createStep, setCreateStep] = useState<1 | 2>(1);
  const [createType, setCreateType] = useState<StorageType>('S3');
  const [createForm, setCreateForm] = useState<VaultForm>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [plans, setPlans] = useState<PlatformPlan[]>([]);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDest, setEditingDest] = useState<StorageDestination | null>(null);
  const [editForm, setEditForm] = useState<VaultForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Credential change confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState<ConfirmationInfo | null>(null);
  const [changeReason, setChangeReason] = useState('');
  const [pendingPayload, setPendingPayload] = useState<Record<string, unknown> | null>(null);

  // Credential history
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyEntries, setHistoryEntries] = useState<CredentialHistoryEntry[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackConfirmVersion, setRollbackConfirmVersion] = useState<number | null>(null);
  const [rollbackReason, setRollbackReason] = useState('');

  // Shared
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  // Storage savings & recalculate
  const [recalculating, setRecalculating] = useState(false);
  const [savingsLoadFailed, setSavingsLoadFailed] = useState(false);
  const [recalculateFailed, setRecalculateFailed] = useState(false);
  const [savings, setSavings] = useState<{
    original_size_gb: number;
    actual_usage_gb: number;
    savings_gb: number;
    savings_percent: number;
  } | null>(null);

  // Type meta with translations
  const getTypeMeta = (): Record<StorageType, { label: string; desc: string; color: string }> => ({
    PLATFORM: {
      label: t('vault.typeLabels.platform'),
      desc: t('vault.types.platform'),
      color: 'brand',
    },
    S3: { label: 'Amazon S3', desc: t('vault.types.s3'), color: 'orange' },
    AZURE_BLOB: { label: 'Azure Blob', desc: t('vault.types.azure'), color: 'blue' },
    GCS: { label: 'Google Cloud', desc: t('vault.types.gcs'), color: 'emerald' },
  });

  useEffect(() => {
    fetchDestinations();
    fetchSavings();
  }, []);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchDestinations = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const res = await api.get('/storage');
      const dests = (res.data.destinations || []).map((d: StorageDestination) => ({
        ...d,
        type: normalizeType(d.type),
      }));
      setDestinations(dests);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value: number, maximumFractionDigits = 2) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: maximumFractionDigits,
      maximumFractionDigits,
    }).format(value);

  const fetchPlans = async () => {
    try {
      const res = await api.get('/storage/plans');
      setPlans(res.data.plans || []);
    } catch {
      setPlans([]);
    }
  };

  const fetchSavings = async () => {
    try {
      const res = await api.get('/storage/savings');
      setSavings(res.data);
      setSavingsLoadFailed(false);
    } catch {
      setSavings(null);
      setSavingsLoadFailed(true);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    setRecalculateFailed(false);
    try {
      await api.post('/storage/recalculate');
      // Refresh after a short delay to allow agents to respond.
      setTimeout(() => {
        fetchDestinations();
        fetchSavings();
        setRecalculating(false);
      }, 3000);
    } catch {
      setRecalculating(false);
      setRecalculateFailed(true);
    }
  };

  // ---------------------------------------------------------------------------
  // Create
  // ---------------------------------------------------------------------------

  const openCreateModal = () => {
    setCreateStep(1);
    setCreateType('S3');
    setCreateForm(EMPTY_FORM);
    setCreateError('');
    setTestResult(null);
    setShowSecrets({});
    setShowCreateModal(true);
    fetchPlans();
  };

  const goToCreateStep2 = () => {
    setCreateForm(EMPTY_FORM);
    setCreateError('');
    if (createType === 'PLATFORM' && plans.length > 0) {
      setCreateForm((prev) => ({ ...prev, selected_plan_gb: plans[0].quota_gb }));
    }
    setCreateStep(2);
  };

  const validateCreateForm = (): string | null => {
    if (!createForm.name.trim()) return t('vault.errors.vaultNameRequired');

    switch (createType) {
      case 'S3':
        if (!createForm.bucket_name.trim()) return t('vault.errors.bucketNameRequired');
        if (!createForm.region.trim()) return t('vault.errors.regionRequired');
        if (!createForm.access_key_id.trim()) return t('vault.errors.accessKeyIdRequired');
        if (!createForm.secret_access_key.trim()) return t('vault.errors.secretAccessKeyRequired');
        if (!createForm.restic_password.trim()) return t('vault.errors.repositoryPasswordRequired');
        break;
      case 'AZURE_BLOB':
        if (!createForm.storage_account_name.trim())
          return t('vault.errors.storageAccountRequired');
        if (!createForm.container_name.trim()) return t('vault.errors.containerRequired');
        if (!createForm.access_key.trim()) return t('vault.errors.accessKeyRequired');
        if (!createForm.restic_password.trim()) return t('vault.errors.repositoryPasswordRequired');
        break;
      case 'GCS':
        if (!createForm.gcs_bucket_name.trim()) return t('vault.errors.bucketNameRequired');
        if (!createForm.json_key.trim()) return t('vault.errors.jsonKeyRequired');
        if (!createForm.restic_password.trim()) return t('vault.errors.repositoryPasswordRequired');
        break;
      case 'PLATFORM':
        if (!createForm.selected_plan_gb) return t('vault.errors.selectPlan');
        break;
    }
    return null;
  };

  const handleCreate = async () => {
    const error = validateCreateForm();
    if (error) {
      setCreateError(error);
      return;
    }

    setCreating(true);
    setCreateError('');

    try {
      const payload: Record<string, unknown> = {
        name: createForm.name.trim(),
        type: createType,
      };

      if (createType === 'S3') {
        // Config fields (non-sensitive)
        payload.config = {
          bucket: createForm.bucket_name.trim(),
          region: createForm.region.trim(),
          ...(createForm.endpoint.trim() && { endpoint: createForm.endpoint.trim() }),
        };
        // Credentials fields (sensitive - encrypted)
        payload.credentials = {
          access_key_id: createForm.access_key_id.trim(),
          secret_access_key: createForm.secret_access_key.trim(),
          restic_password: createForm.restic_password.trim(),
        };
      } else if (createType === 'AZURE_BLOB') {
        // Config fields (non-sensitive)
        payload.config = {
          endpoint: createForm.storage_account_name.trim(),
          container: createForm.container_name.trim(),
        };
        // Credentials fields (sensitive - encrypted)
        payload.credentials = {
          account_name: createForm.storage_account_name.trim(),
          account_key: createForm.access_key.trim(),
          restic_password: createForm.restic_password.trim(),
        };
      } else if (createType === 'GCS') {
        // Config fields (non-sensitive)
        payload.config = {
          bucket: createForm.gcs_bucket_name.trim(),
        };
        // Credentials fields (sensitive - encrypted)
        payload.credentials = {
          json_key: createForm.json_key.trim(),
          restic_password: createForm.restic_password.trim(),
        };
      } else if (createType === 'PLATFORM') {
        payload.config = { quota_gb: createForm.selected_plan_gb };
      }

      await api.post('/storage', payload);
      setShowCreateModal(false);
      fetchDestinations();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? ((err as { response?: { data?: string } }).response?.data ??
            t('vault.errors.creationFailed'))
          : t('vault.errors.creationFailed');
      setCreateError(typeof msg === 'string' ? msg : t('vault.errors.creationFailed'));
    } finally {
      setCreating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit
  // ---------------------------------------------------------------------------

  const openEditModal = async (dest: StorageDestination) => {
    try {
      const res = await api.get(`/storage/${dest.id}`);
      const full: StorageDestination = { ...res.data, type: normalizeType(res.data.type) };
      setEditingDest(full);
      setEditError('');
      setTestResult(null);
      setShowSecrets({});

      const form: VaultForm = { ...EMPTY_FORM, name: full.name, is_default: full.is_default };
      const creds = full.credentials || {};

      if (full.type === 'S3') {
        form.bucket_name = (creds.bucket_name as string) || '';
        form.region = (creds.region as string) || '';
        form.endpoint = (creds.endpoint as string) || '';
      } else if (full.type === 'AZURE_BLOB') {
        form.storage_account_name = (creds.storage_account_name as string) || '';
        form.container_name = (creds.container_name as string) || '';
      } else if (full.type === 'GCS') {
        form.gcs_bucket_name = (creds.bucket_name as string) || '';
      }

      setEditForm(form);
      setShowEditModal(true);
    } catch {
      setEditError(t('vault.errors.loadDetailsFailed'));
    }
  };

  const buildUpdatePayload = (): Record<string, unknown> | null => {
    if (!editingDest) return null;

    const payload: Record<string, unknown> = {};

    if (editForm.name.trim() !== editingDest.name) {
      payload.name = editForm.name.trim();
    }
    if (editForm.is_default !== editingDest.is_default) {
      payload.is_default = editForm.is_default;
    }

    const existingConfig = editingDest.config || {};
    const credentials: Record<string, string> = {};
    const config: Record<string, string> = {};
    let changed = false;

    if (editingDest.type === 'S3') {
      // Config fields (non-sensitive)
      config.bucket = editForm.bucket_name.trim();
      config.region = editForm.region.trim();
      if (editForm.endpoint.trim()) config.endpoint = editForm.endpoint.trim();

      // Credentials fields (sensitive - encrypted)
      credentials.access_key_id = editForm.access_key_id || '••••••••';
      credentials.secret_access_key = editForm.secret_access_key || '••••••••';
      credentials.restic_password = editForm.restic_password || '••••••••';

      changed =
        !!editForm.access_key_id ||
        !!editForm.secret_access_key ||
        !!editForm.restic_password ||
        config.bucket !== (existingConfig.bucket || '') ||
        config.region !== (existingConfig.region || '') ||
        (editForm.endpoint.trim() || '') !== ((existingConfig.endpoint as string) || '');
    } else if (editingDest.type === 'AZURE_BLOB') {
      // Config fields (non-sensitive)
      config.endpoint = editForm.storage_account_name.trim();
      config.container = editForm.container_name.trim();

      // Credentials fields (sensitive - encrypted)
      credentials.account_name = editForm.storage_account_name.trim();
      credentials.account_key = editForm.access_key || '••••••••';
      credentials.restic_password = editForm.restic_password || '••••••••';

      changed =
        !!editForm.access_key ||
        !!editForm.restic_password ||
        config.endpoint !== (existingConfig.endpoint || '') ||
        config.container !== (existingConfig.container || '');
    } else if (editingDest.type === 'GCS') {
      // Config fields (non-sensitive)
      config.bucket = editForm.gcs_bucket_name.trim();

      // Credentials fields (sensitive - encrypted)
      credentials.json_key = editForm.json_key || '••••••••';
      credentials.restic_password = editForm.restic_password || '••••••••';

      changed =
        !!editForm.json_key ||
        !!editForm.restic_password ||
        config.bucket !== (existingConfig.bucket || '');
    }

    if (changed) {
      payload.credentials = credentials;
      payload.config = config;
    }
    return payload;
  };

  const handleUpdate = async () => {
    if (!editingDest) return;
    if (!editForm.name.trim()) {
      setEditError(t('vault.errors.vaultNameRequired'));
      return;
    }

    const payload = buildUpdatePayload();
    if (!payload || Object.keys(payload).length === 0) {
      setShowEditModal(false);
      return;
    }

    setSaving(true);
    setEditError('');

    try {
      await api.put(`/storage/${editingDest.id}`, payload);
      setShowEditModal(false);
      fetchDestinations();
    } catch (err: unknown) {
      // Check for HTTP 428 — requires confirmation.
      const axiosErr = err as { response?: { status?: number; data?: Record<string, unknown> } };
      if (axiosErr?.response?.status === 428 && axiosErr.response.data?.requires_confirmation) {
        const data = axiosErr.response.data;
        setConfirmInfo({
          changing_fields: (data.changing_fields as string[]) || [],
          has_restic_password_change: (data.has_restic_password_change as boolean) || false,
          warning: (data.warning as string) || t('notifications.actionLogged'),
        });
        setPendingPayload(payload);
        setChangeReason('');
        setShowConfirmModal(true);
      } else {
        const msg = axiosErr?.response?.data
          ? typeof axiosErr.response.data === 'string'
            ? axiosErr.response.data
            : t('vault.errors.updateFailed')
          : t('vault.errors.updateFailed');
        setEditError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmedUpdate = async () => {
    if (!editingDest || !pendingPayload) return;
    if (!changeReason.trim()) return;

    setSaving(true);
    setEditError('');

    try {
      const confirmedPayload = {
        ...pendingPayload,
        confirm_credential_change: true,
        change_reason: changeReason.trim(),
      };
      await api.put(`/storage/${editingDest.id}`, confirmedPayload);
      setShowConfirmModal(false);
      setShowEditModal(false);
      setPendingPayload(null);
      setConfirmInfo(null);
      setChangeReason('');
      fetchDestinations();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: string } };
      setEditError(axiosErr?.response?.data ?? t('vault.errors.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Credential history
  // ---------------------------------------------------------------------------

  const fetchCredentialHistory = async (destId: string) => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/storage/${destId}/credential-history?limit=20&offset=0`);
      setHistoryEntries(res.data.history || []);
      setHistoryTotal(res.data.total || 0);
    } catch {
      setHistoryEntries([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryModal = async () => {
    if (!editingDest) return;
    setRollbackConfirmVersion(null);
    setRollbackReason('');
    setShowHistoryModal(true);
    await fetchCredentialHistory(editingDest.id);
  };

  const handleRollback = async (version: number) => {
    if (!editingDest || !rollbackReason.trim()) return;

    setRollingBack(true);
    try {
      await api.post(`/storage/${editingDest.id}/credential-history/${version}/rollback`, {
        confirm_credential_change: true,
        change_reason: rollbackReason.trim(),
      });
      setShowHistoryModal(false);
      setShowEditModal(false);
      setRollbackConfirmVersion(null);
      setRollbackReason('');
      fetchDestinations();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: string } };
      await showError(
        t('vault.errors.rollbackFailed'),
        axiosErr?.response?.data ?? t('vault.errors.rollbackFailed')
      );
    } finally {
      setRollingBack(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Test connection — sends current form credentials so unsaved values work too
  // ---------------------------------------------------------------------------

  const buildTestCredentials = (): {
    credentials?: Record<string, string>;
    config?: Record<string, string>;
  } | null => {
    if (!editingDest) return null;
    const creds: Record<string, string> = {};
    const conf: Record<string, string> = {};

    if (editingDest.type === 'S3') {
      // Config fields (non-sensitive)
      conf.bucket = editForm.bucket_name.trim();
      conf.region = editForm.region.trim();
      if (editForm.endpoint.trim()) conf.endpoint = editForm.endpoint.trim();
      // Credentials fields (sensitive)
      creds.access_key_id = editForm.access_key_id || '';
      creds.secret_access_key = editForm.secret_access_key || '';
      if (editForm.restic_password) creds.restic_password = editForm.restic_password;
      // If secret fields are empty the user didn't change them — don't send inline
      if (!creds.access_key_id || !creds.secret_access_key) return null;
    } else if (editingDest.type === 'AZURE_BLOB') {
      // Config fields (non-sensitive)
      conf.endpoint = editForm.storage_account_name.trim();
      conf.container = editForm.container_name.trim();
      // Credentials fields (sensitive)
      creds.account_name = editForm.storage_account_name.trim();
      creds.account_key = editForm.access_key || '';
      if (editForm.restic_password) creds.restic_password = editForm.restic_password;
      if (!creds.account_key) return null;
    } else if (editingDest.type === 'GCS') {
      // Config fields (non-sensitive)
      conf.bucket = editForm.gcs_bucket_name.trim();
      // Credentials fields (sensitive)
      creds.json_key = editForm.json_key || '';
      if (editForm.restic_password) creds.restic_password = editForm.restic_password;
      if (!creds.json_key) return null;
    }

    return { credentials: creds, config: conf };
  };

  const handleTestConnection = async (destId: string) => {
    setTesting(true);
    setTestResult(null);
    try {
      const inlineCreds = buildTestCredentials();
      const payload = inlineCreds ?? {};
      const res = await api.post(`/storage/${destId}/test`, payload);
      setTestResult(res.data);
    } catch {
      setTestResult({ success: false, message: t('vault.connectionTest.failed') });
    } finally {
      setTesting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------

  const handleDelete = async (id: string) => {
    const confirmed = await showConfirm(
      t('vault.delete.title'),
      t('vault.delete.confirm'),
      'warning'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/storage/${id}`);
      fetchDestinations();
    } catch {
      await showError(t('vault.errors.deleteFailed'), t('vault.errors.deleteFailed'));
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const toggleSecret = (field: string) =>
    setShowSecrets((prev) => ({ ...prev, [field]: !prev[field] }));

  const getIcon = (type: string, size = 24) => {
    const props = { size, className: toneTextClasses[getStorageTypeTone(normalizeType(type))] };
    switch (type) {
      case 'PLATFORM':
        return <Server {...props} />;
      case 'S3':
        return <Cloud {...props} />;
      case 'AZURE_BLOB':
        return <Box {...props} />;
      case 'GCS':
        return <HardDrive {...props} />;
      default:
        return <Database {...props} />;
    }
  };

  // ---------------------------------------------------------------------------
  // Shared field renderers
  // ---------------------------------------------------------------------------

  const renderInput = (
    form: VaultForm,
    setForm: React.Dispatch<React.SetStateAction<VaultForm>>,
    field: keyof VaultForm,
    label: string,
    placeholder: string,
    required = false
  ) => (
    <Input
      id={`storage-${field}`}
      autoComplete="off"
      label={label}
      placeholder={placeholder}
      value={form[field] as string}
      onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
      required={required}
    />
  );

  const renderSecretField = (
    form: VaultForm,
    setForm: React.Dispatch<React.SetStateAction<VaultForm>>,
    field: keyof VaultForm,
    label: string,
    placeholder: string,
    isEditMode: boolean,
    multiline = false
  ) => {
    const isVisible = showSecrets[field] || false;
    const inputId = `storage-${field}`;
    return (
      <div className="w-full">
        <label
          htmlFor={inputId}
          className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1"
        >
          {label}
        </label>
        <div className="relative">
          {multiline ? (
            <textarea
              id={inputId}
              autoComplete="off"
              className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-slate-100 text-sm font-medium font-mono transition-all placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
              rows={4}
              placeholder={placeholder}
              value={form[field] as string}
              onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
            />
          ) : (
            <input
              id={inputId}
              type={isVisible ? 'text' : 'password'}
              autoComplete="new-password"
              className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-slate-100 text-sm font-medium transition-all placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent pr-10"
              placeholder={placeholder}
              value={form[field] as string}
              onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
            />
          )}
          {!multiline && (
            <button
              type="button"
              onClick={() => toggleSecret(field)}
              aria-label={
                isVisible
                  ? t('vault.hideSecret', { name: label })
                  : t('vault.showSecret', { name: label })
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-900 dark:hover:text-white transition-colors"
            >
              {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
        {isEditMode && (
          <p className="mt-1 text-[10px] font-medium text-text-subtle-light dark:text-text-subtle-dark ml-1">
            {t('vault.leaveBlankKeepCurrent')}
          </p>
        )}
      </div>
    );
  };

  const renderConnectionFields = (
    type: StorageType,
    form: VaultForm,
    setForm: React.Dispatch<React.SetStateAction<VaultForm>>,
    isEditMode: boolean
  ) => {
    switch (type) {
      case 'S3':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              {renderInput(
                form,
                setForm,
                'bucket_name',
                t('vault.fieldLabels.bucketName'),
                t('vault.placeholders.bucketName'),
                true
              )}
              {renderInput(
                form,
                setForm,
                'region',
                t('vault.fieldLabels.region'),
                t('vault.placeholders.region'),
                true
              )}
            </div>
            {renderInput(
              form,
              setForm,
              'endpoint',
              t('vault.fieldLabels.customEndpoint'),
              t('vault.placeholders.endpoint')
            )}
            <div className="grid grid-cols-2 gap-4">
              {renderSecretField(
                form,
                setForm,
                'access_key_id',
                t('vault.fieldLabels.accessKeyId'),
                isEditMode
                  ? t('vault.placeholders.accessKeyId')
                  : t('vault.placeholders.accessKeyId'),
                isEditMode
              )}
              {renderSecretField(
                form,
                setForm,
                'secret_access_key',
                t('vault.fieldLabels.secretAccessKey'),
                isEditMode
                  ? t('vault.placeholders.secretAccessKey')
                  : t('vault.placeholders.secretAccessKey'),
                isEditMode
              )}
            </div>
            {renderSecretField(
              form,
              setForm,
              'restic_password',
              t('vault.fieldLabels.repositoryPassword'),
              isEditMode
                ? t('vault.placeholders.repositoryPassword')
                : t('vault.placeholders.repositoryPassword'),
              isEditMode
            )}
          </>
        );

      case 'AZURE_BLOB':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              {renderInput(
                form,
                setForm,
                'storage_account_name',
                t('vault.fieldLabels.storageAccount'),
                t('vault.placeholders.storageAccount'),
                true
              )}
              {renderInput(
                form,
                setForm,
                'container_name',
                t('vault.fieldLabels.container'),
                t('vault.placeholders.container'),
                true
              )}
            </div>
            {renderSecretField(
              form,
              setForm,
              'access_key',
              t('vault.fieldLabels.accessKey'),
              isEditMode ? t('vault.placeholders.accessKey') : t('vault.placeholders.accessKey'),
              isEditMode
            )}
            {renderSecretField(
              form,
              setForm,
              'restic_password',
              t('vault.fieldLabels.repositoryPassword'),
              isEditMode
                ? t('vault.placeholders.repositoryPassword')
                : t('vault.placeholders.repositoryPassword'),
              isEditMode
            )}
          </>
        );

      case 'GCS':
        return (
          <>
            {renderInput(
              form,
              setForm,
              'gcs_bucket_name',
              t('vault.fieldLabels.bucketName'),
              t('vault.placeholders.bucketName'),
              true
            )}
            {renderSecretField(
              form,
              setForm,
              'json_key',
              t('vault.fieldLabels.serviceAccountJsonKey'),
              isEditMode ? t('vault.placeholders.jsonKey') : t('vault.placeholders.jsonKey'),
              isEditMode,
              true
            )}
            {renderSecretField(
              form,
              setForm,
              'restic_password',
              t('vault.fieldLabels.repositoryPassword'),
              isEditMode
                ? t('vault.placeholders.repositoryPassword')
                : t('vault.placeholders.repositoryPassword'),
              isEditMode
            )}
          </>
        );

      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Loading guard
  // ---------------------------------------------------------------------------

  const typeMeta = getTypeMeta();

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary-light dark:text-text-primary-dark tracking-tight">
              {t('vault.title')}
            </h1>
            <p className="text-sm sm:text-base text-text-muted-light dark:text-text-muted-dark mt-1">
              {t('vault.subtitle')}
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
            <Button
              variant="outline"
              onClick={handleRecalculate}
              isLoading={recalculating}
              className="w-full text-text-muted-light dark:text-text-muted-dark sm:w-auto"
            >
              {!recalculating && <RefreshCw size={16} aria-hidden="true" className="mr-2" />}
              {t('vault.recalculate')}
            </Button>
            <Button onClick={openCreateModal} className="w-full shadow-brand-200 sm:w-auto">
              <Plus size={16} aria-hidden="true" className="mr-2" strokeWidth={3} />{' '}
              {t('vault.addVault')}
            </Button>
          </div>
        </div>

        {recalculateFailed && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-4 text-sm text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:flex-row sm:items-center sm:justify-between"
          >
            <p>{t('vault.errors.recalculateFailed')}</p>
            <Button type="button" variant="secondary" onClick={handleRecalculate}>
              {t('vault.retry')}
            </Button>
          </div>
        )}

        {/* Savings banner */}
        {savings && savings.savings_gb > 0 && (
          <div className="p-4 bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 rounded-xl flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-state-ok-light/15 dark:bg-state-ok-dark/15 flex items-center justify-center shrink-0">
              <TrendingDown size={20} className="text-state-ok-light dark:text-state-ok-dark" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-state-ok-light dark:text-state-ok-dark">
                {t('vault.savingsSummary', {
                  amount: `${formatNumber(savings.savings_gb)} GB`,
                  percent: `${formatNumber(savings.savings_percent, 1)}%`,
                })}
              </p>
              <p className="text-xs text-state-ok-light dark:text-state-ok-dark mt-0.5">
                {t('vault.savingsDetails', {
                  original: `${formatNumber(savings.original_size_gb)} GB`,
                  actual: `${formatNumber(savings.actual_usage_gb)} GB`,
                })}
              </p>
            </div>
          </div>
        )}

        {savingsLoadFailed && (
          <div
            role="alert"
            className="flex flex-col gap-3 rounded-xl border border-state-danger-light/20 bg-state-danger-bg-light p-4 text-sm text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark sm:flex-row sm:items-center sm:justify-between"
          >
            <p>{t('vault.errors.savingsLoadFailed')}</p>
            <Button type="button" variant="secondary" onClick={fetchSavings}>
              {t('vault.retry')}
            </Button>
          </div>
        )}

        {/* Grid */}
        {loading && destinations.length === 0 ? (
          <div className="py-16 flex justify-center" role="status" aria-live="polite">
            <span className="sr-only">{t('vault.loading')}</span>
            <div className="animate-spin h-10 w-10 border-b-2 border-brand-600 rounded-full" />
          </div>
        ) : loadFailed ? (
          <div role="alert">
            <Card className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-text-primary-light dark:text-text-primary-dark">
                {t('vault.errors.loadFailed')}
              </p>
              <Button type="button" variant="secondary" onClick={fetchDestinations}>
                {t('vault.retry')}
              </Button>
            </Card>
          </div>
        ) : destinations.length === 0 ? (
          <Card className="py-24 text-center border-2 border-dashed">
            <Database size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">
              {t('vault.noVaultsConfigured')}
            </h3>
            <p className="text-text-muted-light dark:text-text-muted-dark mt-2">
              {t('vault.addVaultDescription')}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
            {destinations.map((dest) => (
              <Card key={dest.id} className="p-6 transition-all hover:border-brand-200 group">
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                      {getIcon(dest.type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-text-primary-light dark:text-text-primary-dark">
                        {dest.type === 'PLATFORM' ? t('vault.platformStorageName') : dest.name}
                      </h3>
                      <p className="text-xs font-medium text-text-subtle-light dark:text-text-subtle-dark">
                        {getTypeMeta()[dest.type as StorageType].label}
                      </p>
                    </div>
                  </div>
                  {dest.is_default && <Badge variant="success">{t('common:status.default')}</Badge>}
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex flex-col gap-1.5 text-xs text-text-muted-light dark:text-text-muted-dark sm:flex-row sm:justify-between sm:text-sm">
                    <span>
                      {dest.type === 'PLATFORM' ? t('vault.platformIncluded') : t('vault.usage')}
                    </span>
                    <span className="font-semibold text-text-primary-light dark:text-text-primary-dark sm:text-right">
                      {formatNumber(dest.storage_used_gb)} GB
                      {dest.storage_quota_gb ? ` / ${formatNumber(dest.storage_quota_gb)} GB` : ''}
                    </span>
                  </div>
                  {typeof dest.storage_quota_gb === 'number' && dest.storage_quota_gb > 0 && (
                    <div
                      role="progressbar"
                      aria-label={t('vault.usage')}
                      aria-valuemin={0}
                      aria-valuemax={dest.storage_quota_gb}
                      aria-valuenow={Math.min(dest.storage_used_gb, dest.storage_quota_gb)}
                      className="w-full bg-surface-border-light dark:bg-surface-border-dark rounded-full h-2 overflow-hidden"
                    >
                      <div
                        className="bg-brand-500 h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min((dest.storage_used_gb / dest.storage_quota_gb) * 100, 100)}%`,
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-surface-border-light dark:border-surface-border-dark">
                  <div className="flex items-center gap-2 text-xs text-text-muted-light dark:text-text-muted-dark">
                    <Activity size={12} className="text-state-ok-light dark:text-state-ok-dark" />{' '}
                    {t('vault.configured')}
                  </div>
                  <div className="flex items-center gap-1">
                    {dest.type !== 'PLATFORM' && (
                      <>
                        <Button
                          variant="ghost"
                          onClick={() => openEditModal(dest)}
                          aria-label={t('vault.editDestination', { name: dest.name })}
                          className="min-h-11 min-w-11 p-2 text-text-subtle-light dark:text-text-subtle-dark hover:text-brand-600 dark:hover:text-brand-400"
                        >
                          <Settings size={16} aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(dest.id)}
                          aria-label={t('vault.deleteVault', { name: dest.name })}
                          className="min-h-11 min-w-11 p-2 text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ================================================================== */}
      {/* CREATE MODAL                                                       */}
      {/* ================================================================== */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <Card className="w-full max-w-xl p-8 shadow-elevated max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {createStep === 1 ? t('vault.addStorageVault') : t('vault.configureVault')}
                </h2>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                  {createStep === 1
                    ? t('vault.chooseStorageType')
                    : t('vault.settingUpStorage', { type: typeMeta[createType].label })}
                </p>
              </div>
              <button
                type="button"
                aria-label={t('common:actions.close')}
                title={t('common:actions.close')}
                onClick={() => setShowCreateModal(false)}
                className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-900 dark:hover:text-white"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-8">
              <div
                className={`h-1.5 flex-1 rounded-full transition-colors ${createStep >= 1 ? 'bg-brand-500' : 'bg-surface-border-light dark:bg-surface-border-dark'}`}
              />
              <div
                className={`h-1.5 flex-1 rounded-full transition-colors ${createStep >= 2 ? 'bg-brand-500' : 'bg-surface-border-light dark:bg-surface-border-dark'}`}
              />
            </div>

            {/* STEP 1 — Type selection */}
            {createStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                  {t('vault.platformStorageInfo')}
                </p>
                {(Object.keys(typeMeta) as StorageType[])
                  .filter((storageType) => storageType !== 'PLATFORM')
                  .map((storageType) => (
                    <button
                      key={storageType}
                      type="button"
                      onClick={() => setCreateType(storageType)}
                      className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                        createType === storageType
                          ? 'border-brand-500 bg-accent-soft-light dark:bg-accent-soft-dark shadow-sm'
                          : 'border-surface-border-light dark:border-surface-border-dark bg-white dark:bg-surface-2-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                      }`}
                    >
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                          createType === storageType
                            ? 'bg-brand-100 dark:bg-brand-800/30'
                            : 'bg-surface-2-light dark:bg-surface-2-dark'
                        }`}
                      >
                        {getIcon(storageType, 22)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-slate-900 dark:text-white">
                          {typeMeta[storageType].label}
                        </p>
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-0.5">
                          {typeMeta[storageType].desc}
                        </p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                          createType === storageType
                            ? 'border-brand-500'
                            : 'border-border-strong-light dark:border-border-strong-dark'
                        }`}
                      >
                        {createType === storageType && (
                          <div className="w-2.5 h-2.5 rounded-full bg-brand-500" />
                        )}
                      </div>
                    </button>
                  ))}

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t('vault.cancel')}
                  </Button>
                  <Button className="flex-1" onClick={goToCreateStep2}>
                    {t('vault.continue')} <ChevronRight size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {/* STEP 2 — Details */}
            {createStep === 2 && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
                className="space-y-5"
              >
                {/* Type badge */}
                <div className="flex items-center gap-3 p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-white dark:bg-surface-2-dark flex items-center justify-center shadow-sm">
                    {getIcon(createType, 18)}
                  </div>
                  <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                    {typeMeta[createType].label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCreateStep(1)}
                    className="ml-auto text-xs text-brand-600 dark:text-brand-400 font-bold hover:underline"
                  >
                    {t('vault.change')}
                  </button>
                </div>

                {/* Name */}
                <Input
                  id="storage-name"
                  label={t('vault.vaultName')}
                  placeholder={
                    createType === 'PLATFORM'
                      ? t('vault.placeholders.vaultNamePlatform')
                      : createType === 'S3'
                        ? t('vault.placeholders.vaultNameS3')
                        : createType === 'AZURE_BLOB'
                          ? t('vault.placeholders.vaultNameAzure')
                          : t('vault.placeholders.vaultNameGCS')
                  }
                  value={createForm.name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />

                {/* Platform plan selection */}
                {createType === 'PLATFORM' && (
                  <div className="space-y-2">
                    <label
                      id="storage-plan-label"
                      className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark ml-1"
                    >
                      {t('vault.storagePlan')}
                    </label>
                    <div
                      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
                      role="group"
                      aria-labelledby="storage-plan-label"
                    >
                      {plans.map((plan) => (
                        <button
                          key={plan.quota_gb}
                          type="button"
                          aria-pressed={createForm.selected_plan_gb === plan.quota_gb}
                          onClick={() =>
                            setCreateForm((prev) => ({
                              ...prev,
                              selected_plan_gb: plan.quota_gb,
                            }))
                          }
                          className={`p-4 rounded-xl border-2 text-center transition-all ${
                            createForm.selected_plan_gb === plan.quota_gb
                              ? 'border-brand-500 bg-accent-soft-light dark:bg-accent-soft-dark'
                              : 'border-surface-border-light dark:border-surface-border-dark bg-white dark:bg-surface-2-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                          }`}
                        >
                          <p className="text-lg font-black text-slate-900 dark:text-white">
                            {plan.name}
                          </p>
                          <p className="text-xs font-bold text-brand-600 dark:text-brand-400 mt-1">
                            ${plan.monthly_cost}/mo
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cloud storage fields */}
                {createType !== 'PLATFORM' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Shield
                        size={14}
                        className="text-text-subtle-light dark:text-text-subtle-dark"
                      />
                      <span className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('vault.connectionDetails')}
                      </span>
                    </div>
                    {renderConnectionFields(createType, createForm, setCreateForm, false)}
                  </div>
                )}

                {/* Security note */}
                <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl flex items-start gap-3">
                  <Shield
                    className="text-state-info-light dark:text-state-info-dark shrink-0 mt-0.5"
                    size={18}
                  />
                  <p className="text-xs font-medium text-state-info-light dark:text-state-info-dark leading-relaxed">
                    {createType === 'PLATFORM'
                      ? t('vault.platformSecurityNote')
                      : t('vault.credentialsSecurityNote')}
                  </p>
                </div>

                {/* Error */}
                {createError && (
                  <div className="p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 rounded-xl flex items-start gap-2">
                    <AlertCircle
                      size={14}
                      className="text-state-danger-light dark:text-state-danger-dark shrink-0 mt-0.5"
                    />
                    <p className="text-xs font-medium text-state-danger-light dark:text-state-danger-dark">
                      {createError}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="secondary"
                    className="flex-1"
                    type="button"
                    onClick={() => setCreateStep(1)}
                  >
                    <ChevronLeft size={16} className="mr-1" /> {t('vault.back')}
                  </Button>
                  <Button className="flex-1" type="submit" isLoading={creating}>
                    {t('vault.createVault')}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      )}

      {/* ================================================================== */}
      {/* EDIT MODAL                                                         */}
      {/* ================================================================== */}
      {showEditModal && editingDest && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <Card className="w-full max-w-xl p-8 shadow-elevated max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-surface-2-light dark:bg-surface-2-dark flex items-center justify-center">
                  {getIcon(editingDest.type, 20)}
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                    {t('vault.editVault')}
                  </h2>
                  <p className="text-xs font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                    {typeMeta[editingDest.type as StorageType]?.label || editingDest.type}
                  </p>
                </div>
              </div>
              <button
                type="button"
                aria-label={t('common:actions.close')}
                title={t('common:actions.close')}
                onClick={() => setShowEditModal(false)}
                className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-900 dark:hover:text-white"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleUpdate();
              }}
              className="space-y-5"
            >
              {/* Name */}
              <Input
                label={t('vault.vaultName')}
                placeholder={t('vault.placeholders.vaultNameS3')}
                value={editForm.name}
                onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />

              {/* Default toggle */}
              <div className="flex items-center justify-between p-4 bg-surface-2-light dark:bg-surface-2-dark rounded-xl">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    {t('vault.defaultStorage')}
                  </p>
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-0.5">
                    {t('vault.defaultStorageDescription')}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-label={t('vault.defaultStorage')}
                  aria-checked={editForm.is_default}
                  onClick={() => setEditForm((prev) => ({ ...prev, is_default: !prev.is_default }))}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                >
                  <span
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editForm.is_default
                        ? 'bg-brand-600'
                        : 'bg-border-strong-light dark:bg-border-strong-dark'
                    }`}
                    aria-hidden="true"
                  >
                    <span
                      className={`inline-block h-4 w-4 rounded-full bg-white transition-transform shadow-sm ${
                        editForm.is_default ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </span>
                </button>
              </div>

              {/* Connection fields */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Shield size={14} className="text-text-subtle-light dark:text-text-subtle-dark" />
                  <span className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                    {t('vault.connectionDetails')}
                  </span>
                </div>
                {renderConnectionFields(
                  editingDest.type as StorageType,
                  editForm,
                  setEditForm,
                  true
                )}
              </div>

              {/* Test connection + History */}
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestConnection(editingDest.id)}
                    isLoading={testing}
                    className="flex-1"
                  >
                    <Wifi size={14} className="mr-2" /> {t('vault.testConnection')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={openHistoryModal}
                    className="shrink-0"
                  >
                    <Clock size={14} className="mr-2" /> {t('vault.history')}
                  </Button>
                </div>

                {testResult && (
                  <div
                    className={`p-3 rounded-xl flex items-start gap-2 text-xs font-medium ${
                      testResult.success
                        ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark text-state-ok-light dark:text-state-ok-dark border border-state-ok-light/20'
                        : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark text-state-danger-light dark:text-state-danger-dark border border-state-danger-light/20'
                    }`}
                  >
                    {testResult.success ? (
                      <CheckCircle size={14} className="shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    )}
                    {testResult.message}
                  </div>
                )}
              </div>

              {/* Error */}
              {editError && (
                <div className="p-3 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 rounded-xl flex items-start gap-2">
                  <AlertCircle
                    size={14}
                    className="text-state-danger-light dark:text-state-danger-dark shrink-0 mt-0.5"
                  />
                  <p className="text-xs font-medium text-state-danger-light dark:text-state-danger-dark">
                    {editError}
                  </p>
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl flex items-start gap-3">
                <Shield
                  className="text-state-info-light dark:text-state-info-dark shrink-0 mt-0.5"
                  size={18}
                />
                <p className="text-xs font-medium text-state-info-light dark:text-state-info-dark leading-relaxed">
                  {t('vault.credentialsEncryptedNote')}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  type="button"
                  onClick={() => setShowEditModal(false)}
                >
                  {t('vault.cancel')}
                </Button>
                <Button className="flex-1" type="submit" isLoading={saving}>
                  {t('vault.saveChanges')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
      {/* ================================================================== */}
      {/* CREDENTIAL CHANGE CONFIRMATION MODAL                              */}
      {/* ================================================================== */}
      {showConfirmModal && confirmInfo && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in">
          <Card className="w-full max-w-md p-8 shadow-elevated">
            <div className="flex items-start gap-4 mb-6">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  confirmInfo.has_restic_password_change
                    ? 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark'
                    : 'bg-state-warn-bg-light dark:bg-state-warn-bg-dark'
                }`}
              >
                <AlertTriangle
                  size={24}
                  className={
                    confirmInfo.has_restic_password_change
                      ? 'text-state-danger-light dark:text-state-danger-dark'
                      : 'text-state-warn-light dark:text-state-warn-dark'
                  }
                />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('vault.confirmCredentialChange')}
                </h2>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                  {t('notifications.actionLogged')}
                </p>
              </div>
            </div>

            {/* Changing fields badges */}
            <div className="flex flex-wrap gap-2 mb-4">
              {confirmInfo.changing_fields.map((field) => (
                <Badge key={field} variant={field === 'restic_password' ? 'error' : 'neutral'}>
                  {field.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>

            {/* Warning */}
            {confirmInfo.has_restic_password_change && (
              <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-2 border-state-danger-light/30 dark:border-state-danger-dark/30 rounded-xl mb-4">
                <p className="text-sm font-bold text-state-danger-light dark:text-state-danger-dark leading-relaxed">
                  {t('vault.warnings.dangerPrefix')} {confirmInfo.warning}
                </p>
              </div>
            )}

            {!confirmInfo.has_restic_password_change && (
              <div className="p-4 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/30 dark:border-state-warn-dark/30 rounded-xl mb-4">
                <p className="text-sm font-medium text-state-warn-light dark:text-state-warn-dark leading-relaxed">
                  {confirmInfo.warning}
                </p>
              </div>
            )}

            {/* Reason input */}
            <div className="mb-6">
              <label className="block text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 ml-1">
                {t('vault.reasonForChange')}{' '}
                <span className="text-state-danger-light dark:text-state-danger-dark">*</span>
              </label>
              <textarea
                className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-slate-900 dark:text-slate-100 text-sm font-medium transition-all placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
                rows={3}
                placeholder={t('vault.placeholders.reasonForChange')}
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingPayload(null);
                  setConfirmInfo(null);
                  setChangeReason('');
                }}
              >
                {t('vault.cancel')}
              </Button>
              <Button
                variant={confirmInfo.has_restic_password_change ? 'danger' : 'primary'}
                className="flex-1"
                onClick={handleConfirmedUpdate}
                isLoading={saving}
                disabled={!changeReason.trim()}
              >
                {confirmInfo.has_restic_password_change
                  ? t('vault.understandChangeCredentials')
                  : t('vault.confirmChange')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ================================================================== */}
      {/* CREDENTIAL HISTORY MODAL                                          */}
      {/* ================================================================== */}
      {showHistoryModal && editingDest && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <Card className="w-full max-w-lg p-8 shadow-elevated max-h-[85vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  {t('vault.credentialHistory')}
                </h2>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                  {historyTotal} {t('vault.changesRecorded')}
                </p>
              </div>
              <button
                type="button"
                aria-label={t('common:actions.close')}
                title={t('common:actions.close')}
                onClick={() => {
                  setShowHistoryModal(false);
                  setRollbackConfirmVersion(null);
                  setRollbackReason('');
                }}
                className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-lg text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-900 dark:hover:text-white"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>

            {historyLoading ? (
              <div className="py-12 flex justify-center">
                <div className="animate-spin h-8 w-8 border-b-2 border-brand-600 rounded-full" />
              </div>
            ) : historyEntries.length === 0 ? (
              <div className="py-12 text-center">
                <Clock size={32} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-sm text-text-muted-light dark:text-text-muted-dark font-medium">
                  {t('vault.noCredentialChanges')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {historyEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="p-4 border border-surface-border-light dark:border-surface-border-dark rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest">
                          {t('vault.version')}
                          {entry.version}
                        </span>
                        <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                          {new Date(entry.created_at).toLocaleString()}
                        </span>
                      </div>
                      {rollbackConfirmVersion !== entry.version && (
                        <button
                          type="button"
                          onClick={() => {
                            setRollbackConfirmVersion(entry.version);
                            setRollbackReason('');
                          }}
                          className="text-xs font-bold text-brand-600 dark:text-brand-400 hover:text-brand-700 flex items-center gap-1"
                        >
                          <RotateCcw size={12} /> {t('vault.rollback')}
                        </button>
                      )}
                    </div>

                    {/* Changed fields */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {entry.changed_fields.map((f) => (
                        <span
                          key={f}
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            f === 'restic_password'
                              ? toneChipClasses.danger
                              : f === 'rollback'
                                ? toneChipClasses.info
                                : toneChipClasses.neutral
                          }`}
                        >
                          {f.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>

                    {/* Who & why */}
                    {entry.changed_by_name && (
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('vault.by')} {entry.changed_by_name}
                      </p>
                    )}
                    {entry.change_reason && (
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1 italic">
                        &ldquo;{entry.change_reason}&rdquo;
                      </p>
                    )}

                    {/* Inline rollback confirmation */}
                    {rollbackConfirmVersion === entry.version && (
                      <div className="mt-3 pt-3 border-t border-surface-border-light dark:border-surface-border-dark space-y-3">
                        <div className="p-3 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/30 dark:border-state-warn-dark/30 rounded-lg">
                          <p className="text-xs font-bold text-state-warn-light dark:text-state-warn-dark">
                            {t('vault.rollbackDescription')} {entry.version}.
                          </p>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-1">
                            {t('vault.reasonForChange')}{' '}
                            <span className="text-state-danger-light dark:text-state-danger-dark">
                              *
                            </span>
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg text-sm text-slate-900 dark:text-slate-100 placeholder:text-text-subtle-light dark:placeholder:text-text-subtle-dark focus:outline-none focus:ring-2 focus:ring-brand-500"
                            placeholder={t('vault.rollbackReason')}
                            value={rollbackReason}
                            onChange={(e) => setRollbackReason(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              setRollbackConfirmVersion(null);
                              setRollbackReason('');
                            }}
                          >
                            {t('vault.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            className="flex-1"
                            onClick={() => handleRollback(entry.version)}
                            isLoading={rollingBack}
                            disabled={!rollbackReason.trim()}
                          >
                            {t('vault.confirmRollback')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
